import { useState, useEffect } from "react";
import { CalendarPlus, Search, Clock, Video, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { leadsApi } from "@/lib/crmApi";

/**
 * Calendar-page entry point to create an RDV from scratch:
 *   1. Pick a lead from the existing list (search box + result picker)
 *   2. Pick date + time
 *   3. Pick physique / visio
 *   4. Save
 *
 * Uses PUT /leads/{id} with `rdv_at`, `rdv_kind`, `conversation_status` so the
 * calendar / inbox / dashboard pick it up immediately.
 *
 * Optional `defaultDate` (Date) prefills the picker — handy when called from
 * a "click on a day" handler in the future.
 */
export default function NewRdvButton({ leads, onCreated, defaultDate }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [leadId, setLeadId] = useState("");
  const [datetime, setDatetime] = useState("");
  const [kind, setKind] = useState("physique");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form on open
  useEffect(() => {
    if (open) {
      setSearch("");
      setLeadId("");
      setKind("physique");
      setErrors({});
      // Prefill the picker with `defaultDate` if provided, or 9:00 tomorrow
      // otherwise so the user just edits the hour.
      const base = defaultDate ? new Date(defaultDate) : new Date();
      if (!defaultDate) {
        base.setDate(base.getDate() + 1);
        base.setHours(9, 0, 0, 0);
      }
      setDatetime(toLocalInput(base.toISOString()));
    }
  }, [open, defaultDate]);

  const filteredLeads = (leads || []).filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (l.nom || "").toLowerCase().includes(q) ||
      (l.telephone || "").includes(q.replace(/[^0-9]/g, "")) ||
      (l.programme_immobilier || "").toLowerCase().includes(q)
    );
  });

  const selectedLead = leads?.find((l) => l.id === leadId) || null;

  const save = async () => {
    const next = {};
    if (!leadId) next.leadId = "Choisis un lead";
    if (!datetime) next.datetime = "Choisis une date et une heure";
    let isoUtc = null;
    if (datetime) {
      isoUtc = toIsoUtc(datetime);
      if (!isoUtc) next.datetime = "Date invalide";
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      toast.error(Object.values(next)[0]);
      return;
    }
    if (new Date(isoUtc).getTime() < Date.now() - 60 * 60 * 1000) {
      if (!window.confirm("Cette date est dans le passé. Continuer ?")) return;
    }
    setSaving(true);
    try {
      await leadsApi.update(leadId, {
        rdv_at: isoUtc,
        rdv_kind: kind,
        conversation_status: "rdv_confirme",
      });
      toast.success(`RDV créé · ${formatNice(isoUtc)}`);
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-1.5"
        data-testid="calendar-new-rdv-btn"
      >
        <CalendarPlus className="h-4 w-4" />
        Nouveau RDV
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Planifier un rendez-vous</DialogTitle>
            <DialogDescription>
              Sélectionne un lead, puis fixe la date et l'heure exactes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Error banner — surfaces every required field that's missing. */}
            {Object.values(errors).filter(Boolean).length > 0 && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
                data-testid="newrdv-error-banner"
              >
                <span className="flex-none font-bold">⚠️</span>
                <div>
                  <p className="font-semibold">Champs manquants</p>
                  <ul className="ml-4 list-disc">
                    {Object.entries(errors)
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <li key={k}>{v}</li>
                      ))}
                  </ul>
                </div>
              </div>
            )}
            {/* Lead picker */}
            <div className="space-y-1.5">
              <Label className={`text-xs ${errors.leadId ? "text-red-600" : ""}`}>
                Lead{errors.leadId && " *"}
              </Label>
              {selectedLead ? (
                <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm dark:border-emerald-900/40 dark:bg-emerald-950/30">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{selectedLead.nom}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {selectedLead.telephone}
                      {selectedLead.programme_immobilier
                        ? ` · ${selectedLead.programme_immobilier}`
                        : ""}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLeadId("")}
                    className="text-xs"
                  >
                    Changer
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Rechercher par nom, téléphone, programme…"
                      className="pl-9"
                      data-testid="newrdv-lead-search"
                    />
                  </div>
                  <ul className={`max-h-44 space-y-0.5 overflow-y-auto rounded-lg border bg-muted/20 p-1 ${
                    errors.leadId ? "border-red-500" : "border-border/60"
                  }`}>
                    {filteredLeads.length === 0 ? (
                      <li className="p-2 text-xs italic text-muted-foreground">
                        Aucun lead trouvé.
                      </li>
                    ) : (
                      filteredLeads.slice(0, 50).map((l) => (
                        <li key={l.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setLeadId(l.id);
                              if (errors.leadId)
                                setErrors((p) => ({ ...p, leadId: null }));
                            }}
                            className="w-full rounded p-1.5 text-left text-sm hover:bg-muted/60"
                            data-testid={`newrdv-lead-${l.id}`}
                          >
                            <div className="truncate font-medium">{l.nom}</div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {l.telephone}
                              {l.programme_immobilier && ` · ${l.programme_immobilier}`}
                            </div>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </>
              )}
              {errors.leadId && (
                <p className="text-[11px] font-medium text-red-600">{errors.leadId}</p>
              )}
            </div>

            {/* Date + time */}
            <div className="space-y-1.5">
              <Label className={`flex items-center gap-1.5 text-xs ${
                errors.datetime ? "text-red-600" : ""
              }`}>
                <Clock className="h-3 w-3" />
                Date et heure (Africa/Dakar){errors.datetime && " *"}
              </Label>
              <Input
                type="datetime-local"
                value={datetime}
                onChange={(e) => {
                  setDatetime(e.target.value);
                  if (errors.datetime) setErrors((p) => ({ ...p, datetime: null }));
                }}
                className={`font-mono ${
                  errors.datetime ? "border-red-500 ring-1 ring-red-500" : ""
                }`}
                step="900"
                data-testid="newrdv-datetime"
              />
              {errors.datetime && (
                <p className="text-[11px] font-medium text-red-600">{errors.datetime}</p>
              )}
            </div>

            {/* Type */}
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="newrdv-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="physique">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5" />
                      Physique
                    </span>
                  </SelectItem>
                  <SelectItem value="visio">
                    <span className="inline-flex items-center gap-2">
                      <Video className="h-3.5 w-3.5" />
                      Visio
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {datetime && (
              <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs">
                Aperçu : <span className="font-medium">{formatNice(toIsoUtc(datetime))}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={save} disabled={saving} data-testid="newrdv-save">
              {saving ? "Enregistrement…" : "Créer le RDV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- shared format helpers (kept inline so this file is self-contained) ---

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      "T" +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes())
    );
  } catch {
    return "";
  }
}

function toIsoUtc(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function formatNice(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
